/**
 * The Ledger Button, as Chakra's `button` recipe. Every raw `<Button>` and
 * `<IconButton>` reads it, so this one file restyles every button in the app.
 *
 * Variants keep Chakra's names so existing call sites need no edit:
 * - `solid` is the primary button; `colorPalette="orange"` (the old call to
 *   action) and `"accent"` both resolve to the accent fill, `"red"` is danger.
 * - `outline` is the secondary button: surface, 1px border.
 * - `ghost` has no fill until hovered; with `colorPalette="red"` it is the
 *   danger-ghost button.
 * The `~/components/ui/button` wrapper also accepts the Ledger names
 * (primary, secondary, danger, danger-ghost) and maps them onto these.
 *
 * Sizes (docs/design-system/TOKEN-MAP.md 8d): `2xs`/`xs` 24, `sm`/`md` 28
 * (the default), `lg` and up 32.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Components 1: primitives".
 */
import { defineRecipe } from "@chakra-ui/react";
import { compactPressArea, disabledControl, focusRing } from "./shared";

const size = ({
  height,
  paddingX,
  fontSize,
  icon,
  gap,
}: {
  height: string;
  paddingX: string;
  fontSize: string;
  icon: string;
  gap: string;
}) => ({
  h: height,
  minW: height,
  px: paddingX,
  gap,
  fontSize,
  lineHeight: "var(--leading-tight)",
  textStyle: "none",
  _icon: {
    flexShrink: 0,
    width: "auto",
    height: "auto",
    maxWidth: icon,
    maxHeight: icon,
  },
});

// 24 and 28px buttons press at 32px below 1024 (P-20); 32px ones need nothing.
const small = {
  ...size({
    height: "var(--control-sm)",
    paddingX: "var(--space-2)",
    fontSize: "var(--text-12)",
    icon: "var(--icon-sm)",
    gap: "var(--space-1)",
  }),
  ...compactPressArea,
};
const medium = {
  ...size({
    height: "var(--control-md)",
    paddingX: "var(--space-3)",
    fontSize: "var(--text-13)",
    icon: "var(--icon-sm)",
    gap: "var(--space-2)",
  }),
  ...compactPressArea,
};
const large = size({
  height: "var(--control-lg)",
  paddingX: "var(--space-4)",
  fontSize: "var(--text-13)",
  icon: "var(--icon-md)",
  gap: "var(--space-2)",
});

export const buttonRecipe = defineRecipe({
  base: {
    fontFamily: "var(--font-sans)",
    fontWeight: "var(--weight-medium)",
    borderRadius: "var(--radius-2)",
    transitionProperty: "background-color, border-color, color, box-shadow",
    transitionDuration: "var(--dur-1)",
    transitionTimingFunction: "var(--ease-out)",
    focusVisibleRing: "none",
    _focusVisible: focusRing,
    _disabled: {
      ...disabledControl,
      // Chakra's `layerStyle: disabled` fades the whole control; the Ledger
      // draws it in the disabled colours instead, so it stays legible.
      layerStyle: "none",
    },
    // Busy: the wrapper swaps the icon for a spinner and keeps the label; the
    // button holds its colours and stops taking presses.
    "&[aria-busy=true]": { cursor: "progress" },
  },
  variants: {
    variant: {
      solid: {
        bg: "colorPalette.solid",
        color: "colorPalette.contrast",
        borderColor: "transparent",
        _hover: { bg: "colorPalette.hover" },
        _active: { bg: "colorPalette.emphasized" },
        _expanded: { bg: "colorPalette.hover" },
        _disabled: { ...disabledControl, borderColor: "transparent" },
      },
      outline: {
        bg: "var(--color-surface)",
        color: "var(--color-fg)",
        borderColor: "var(--color-border)",
        boxShadow: "none",
        _hover: {
          bg: "var(--color-surface-3)",
          boxShadow: "none",
        },
        _active: {
          bg: "var(--color-surface-3)",
          borderColor: "var(--color-border-strong)",
        },
        _expanded: { bg: "var(--color-surface-3)" },
        _disabled: disabledControl,
      },
      ghost: {
        bg: "transparent",
        color: "var(--color-fg)",
        borderColor: "transparent",
        _hover: { bg: "var(--color-surface-3)" },
        _active: { bg: "var(--color-surface-3)" },
        _expanded: { bg: "var(--color-surface-3)" },
        _disabled: {
          ...disabledControl,
          background: "transparent",
          borderColor: "transparent",
        },
      },
    },
    size: {
      "2xs": small,
      xs: small,
      sm: medium,
      md: medium,
      lg: large,
      xl: large,
      "2xl": large,
    },
  },
  defaultVariants: {
    size: "sm",
  },
});
